import type { Condition, ConditionGroup, RuleConditions } from '../types/rule';
import type { ParsedBody } from '../types/parsed-body';

export interface UnifiedRequestContext {
  method: string;
  path: string;
  headers: Record<string, string>;
  query: Record<string, string>;
  body: string | null;
  parsedBody?: ParsedBody;
}

/** Maximum nesting depth for condition groups. */
export const MAX_CONDITION_DEPTH = 5;

export function isConditionGroup(node: Condition | ConditionGroup): node is ConditionGroup {
  return typeof node === 'object' && node !== null && 'logic' in node;
}

export function evaluateConditions(
  conditions: RuleConditions | undefined,
  context: UnifiedRequestContext,
): boolean {
  if (!conditions) return true;
  if (Array.isArray(conditions)) {
    // Flat list — shorthand for an AND group
    return conditions.every((c) => evaluateSingleCondition(c, context));
  }
  return evaluateGroup(conditions, context);
}

function evaluateGroup(group: ConditionGroup, context: UnifiedRequestContext): boolean {
  let result: boolean;
  if (group.conditions.length === 0) {
    // Empty groups always match, consistent with an empty flat list
    result = true;
  } else if (group.logic === 'or') {
    result = group.conditions.some((node) => evaluateNode(node, context));
  } else {
    result = group.conditions.every((node) => evaluateNode(node, context));
  }
  return group.negate ? !result : result;
}

function evaluateNode(node: Condition | ConditionGroup, context: UnifiedRequestContext): boolean {
  return isConditionGroup(node)
    ? evaluateGroup(node, context)
    : evaluateSingleCondition(node, context);
}

/**
 * Structural validation for user-supplied conditions. Throws with a
 * user-facing message on malformed shapes or excessive nesting.
 */
export function validateConditions(conditions: RuleConditions | undefined): void {
  if (!conditions) return;
  if (Array.isArray(conditions)) {
    for (const c of conditions) {
      if (typeof c !== 'object' || c === null) {
        throw new Error('Conditions must be objects');
      }
      if (isConditionGroup(c)) {
        throw new Error('A flat condition list cannot contain groups; use a top-level group instead');
      }
    }
    return;
  }
  validateGroup(conditions, 1);
}

function validateGroup(group: ConditionGroup, depth: number): void {
  if (depth > MAX_CONDITION_DEPTH) {
    throw new Error(`Condition groups cannot nest deeper than ${MAX_CONDITION_DEPTH} levels`);
  }
  if (group.logic !== 'and' && group.logic !== 'or') {
    throw new Error(`Invalid condition group logic: ${String(group.logic)}`);
  }
  if (!Array.isArray(group.conditions)) {
    throw new Error('Condition group must have a conditions array');
  }
  for (const node of group.conditions) {
    if (typeof node !== 'object' || node === null) {
      throw new Error('Conditions must be objects');
    }
    if (isConditionGroup(node)) {
      validateGroup(node, depth + 1);
    }
  }
}

function evaluateSingleCondition(condition: Condition, context: UnifiedRequestContext): boolean {
  const targetValue = resolveTarget(condition, context);

  switch (condition.operator) {
    case 'exists':
      return targetValue !== null && targetValue !== undefined;
    case 'not_exists':
      return targetValue === null || targetValue === undefined;
    case 'equals':
      return targetValue === condition.value;
    case 'not_equals':
      return targetValue !== condition.value;
    case 'contains':
      return targetValue != null && condition.value != null && targetValue.includes(condition.value);
    case 'not_contains':
      return targetValue == null || condition.value == null || !targetValue.includes(condition.value);
    case 'matches_regex': {
      if (targetValue == null || condition.value == null) return false;
      try {
        return new RegExp(condition.value).test(targetValue);
      } catch {
        return false;
      }
    }
    case 'less_than': {
      // For 'random' target: compare Math.random()*100 < value
      if (condition.target === 'random') {
        const threshold = parseFloat(condition.value ?? '0');
        if (threshold <= 0) return false;
        if (threshold >= 100) return true;
        return Math.random() * 100 < threshold;
      }
      // For other targets: numeric comparison
      if (targetValue == null || condition.value == null) return false;
      return parseFloat(targetValue) < parseFloat(condition.value);
    }
    default:
      return false;
  }
}

function resolveTarget(condition: Condition, context: UnifiedRequestContext): string | null {
  switch (condition.target) {
    case 'method':
      return context.method;
    case 'path':
      return context.path;
    case 'header': {
      if (!condition.key) return null;
      const lowerKey = condition.key.toLowerCase();
      return context.headers[lowerKey] ?? context.headers[condition.key] ?? null;
    }
    case 'query': {
      if (!condition.key) return null;
      return context.query[condition.key] ?? null;
    }
    case 'body_raw':
      if (context.parsedBody?.raw !== undefined) {
        return context.parsedBody.raw;
      }
      return context.body;
    case 'body_json': {
      if (!context.body || !condition.key) return null;
      try {
        const parsed = JSON.parse(context.body);
        return resolveJsonPath(parsed, condition.key);
      } catch {
        return null;
      }
    }
    case 'body_field': {
      if (!condition.key || !context.parsedBody) return null;
      const value = context.parsedBody.fields[condition.key];
      return value !== undefined ? value : null;
    }
    case 'random':
      // Random is handled in the operator switch, not here
      return null;
    default:
      return null;
  }
}

function resolveJsonPath(obj: unknown, path: string): string | null {
  const parts = path.split('.');
  let current: unknown = obj;

  for (const part of parts) {
    if (current === null || current === undefined || typeof current !== 'object') {
      return null;
    }
    current = (current as Record<string, unknown>)[part];
  }

  if (current === null || current === undefined) return null;
  return typeof current === 'string' ? current : JSON.stringify(current);
}
